import {
  campaignsService,
  getCampaignSourceId,
  getCampaignSourceType
} from '@services/domain-factories/campaigns';
import { expressionsService } from '@services/domain-factories/expressions';
import { formGroupsService } from '@services/domain-factories/formGroups';
import { formModelsService } from '@services/domain-factories/formModels';
import { formsService } from '@services/domain-factories/forms';
import { membersService } from '@services/domain-factories/members';
import { tableService } from '@services/domain-factories/tables/tables';
import { usersService } from '@services/domain-factories/users';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import {
  Campaign,
  Expression,
  Form,
  FormGroup,
  FormModel,
  InvitationEnvelope,
  Member,
  SurveyResponse,
  Table,
  TableForm,
  TableItem,
  User
} from '@vincerosoft/assessfluence-types';
import { BaseForm } from '@vincerosoft/assessfluence-types/build/src/types/forms/form.interface';

export type SurveyRenderData = {
  forms: Form[];
  formGroups: FormGroup[];
  formModels: FormModel[];
  respondents: SurveyResponse['respondent'][];
  campaigns: Campaign[];
  expressions: Expression[];
  linkedTables: {
    [tableFormId: string]: {
      items: TableItem[];
      tableForm: TableForm;
      formModel: FormModel;
      table: Table;
    };
  };
};

export async function getTableItemDataFactory(tffId: string, table_item_id: string) {
  const tableFactoryForm = await collectionsService.forms.getDoc<TableForm>(tffId);
  if (!tableFactoryForm) {
    return;
  }
  const formModel = await formModelsService.getFormModelByFormId(tffId);
  if (!formModel) {
    return;
  }
  const table = await tableService.getTableByFormId(tffId);
  if (!table) {
    return;
  }
  const tableItem = await tableService.getTableItem(table.table_name, table_item_id);

  if (!tableItem) {
    return;
  }

  return {
    tableForm: tableFactoryForm,
    formModel: formModel,
    table,
    item: tableItem as TableItem
  };
}

export async function surveyRenderDataFactory<T extends 'invitation' | 'response' | 'workspace'>(
  dataOrigins: T extends 'invitation'
    ? InvitationEnvelope[]
    : T extends 'response'
      ? SurveyResponse[]
      : Form[],
  sourceType: T,
  linkedCampaignIds: T extends 'invitation' | 'response' ? string[] : never
) {
  const inFlightStore: SurveyRenderData = {
    forms: [],
    formGroups: [],
    formModels: [],
    respondents: [],
    campaigns: [],
    expressions: [],
    linkedTables: {}
  };

  inFlightStore.campaigns =
    linkedCampaignIds.length > 0 ? await campaignsService.getCampaignsByIds(linkedCampaignIds) : [];

  let formIds = new Set<string>();
  if (sourceType === 'workspace') {
    formIds = new Set(dataOrigins.map((form) => form.id));
  }

  const formGroupIds = new Set<string>();
  const userIdSet = new Set<string>();
  const memberIdSet = new Set<string>();
  const linkedTableItemIds: {
    sourceId: string;
    sourceType: Campaign['resource']['type'];
    table_item_id: string;
  }[] = [];

  dataOrigins.forEach((item) => {
    if (sourceType === 'workspace') {
      return;
    }
    const campaignId =
      sourceType === 'invitation'
        ? (item as InvitationEnvelope).linked_campaign?.id
        : (item as SurveyResponse).campaign?.id;
    const campaign = inFlightStore.campaigns.find((c) => c.id === campaignId);

    const userId =
      sourceType === 'invitation'
        ? (item as InvitationEnvelope & { user_id: string }).user_id
        : (item as SurveyResponse).respondent?.user_id;

    if (userId) {
      userIdSet.add(userId);
    }

    const memberId =
      sourceType === 'invitation'
        ? (item as InvitationEnvelope).member_id
        : (item as SurveyResponse).respondent?.member_id;

    if (memberId) {
      memberIdSet.add(memberId);
    }

    if (campaign) {
      const campaignSourceId = getCampaignSourceId(campaign);
      const campaignSourceType = getCampaignSourceType(campaign);
      if (campaign?.table_item_id) {
        linkedTableItemIds.push({
          sourceId: campaignSourceId,
          sourceType: campaignSourceType,
          table_item_id: campaign.table_item_id
        });
      }
      if (campaignSourceType === 'form-group') {
        formGroupIds.add(campaignSourceId);
      } else {
        formIds.add(campaignSourceId);
      }
    } else {
      const response = item as SurveyResponse;
      const formId = response.form.id as string;
      if (response.table_item_id) {
        linkedTableItemIds.push({
          sourceId: formId,
          sourceType: 'form',
          table_item_id: response.table_item_id
        });
      }

      formIds.add(formId);
    }
  });

  const respondentBatches: SurveyResponse['respondent'][] = [];
  const users: User[] = [];
  const members: Member[] = [];

  if (userIdSet.size > 0) {
    const fetchedUsers = await usersService.getUsersByIds(Array.from(userIdSet));
    users.push(...fetchedUsers);

    fetchedUsers
      .map((u) => u.member_ids)
      .flat()
      .forEach((id) => memberIdSet.add(id));
  }

  if (memberIdSet.size > 0) {
    const fetchedMembers = await membersService.getMembersByIds(Array.from(memberIdSet));
    members.push(...fetchedMembers);

    const userIds = fetchedMembers.map((m) => m.user_id).filter(Boolean);

    if (userIds.length > 0) {
      const fetchedUserIds = new Set(users.map((u) => u.id));
      const missingUserIds = userIds.filter((id) => !fetchedUserIds.has(id));
      const missingUsers = await usersService.getUsersByIds(missingUserIds);
      users.push(...missingUsers);
    }
  }

  members.forEach((member) => {
    const user = users.find((u) => u.id === member.user_id);
    if (user) {
      respondentBatches.push({
        member_id: member.id,
        user_id: user.id,
        name: user.name,
        email: user.email,
        uid: user.uid
      });
    }
  });

  // 2. Strip top-level document id while preserving rest of object
  inFlightStore.respondents = respondentBatches;

  if (formGroupIds.size > 0) {
    const formGroupIdsArray = Array.from(formGroupIds);
    const formGroupsChunks = await collectionsService.formGroups.getDocsInBatches([
      'id',
      'in',
      formGroupIdsArray
    ]);
    inFlightStore.formGroups = formGroupsChunks.flat();
    const linkedFormIds = formGroupsService.extractLinkedFormIds(inFlightStore.formGroups);

    formIds = new Set([...Array.from(formIds), ...linkedFormIds]);
  }

  if (formIds.size > 0) {
    const formIdsArray = Array.from(formIds);
    await formsService.getFormsByIds('invitationsFactory', formIdsArray, undefined, (data) =>
      inFlightStore.forms.push(...data)
    );
    inFlightStore.expressions =
      await expressionsService.getRuntimeExpressionsBySourceIds(formIdsArray);
    inFlightStore.formModels = await formModelsService.getFormModelByFormIds(formIdsArray);
  }

  if (linkedTableItemIds.length > 0) {
    for (const { sourceId, sourceType, table_item_id } of linkedTableItemIds) {
      let sourceDoc: Form | FormGroup | null | undefined = null;
      if (sourceType === 'form') {
        sourceDoc = inFlightStore.forms.find((f) => f.id === sourceId);
      } else if (sourceType === 'form-group') {
        sourceDoc = inFlightStore.formGroups.find((fg) => fg.id === sourceId);
      }
      if (!sourceDoc) {
        continue;
      }

      const linkedTableFactoryFormId = (sourceDoc as BaseForm)?.options?.linked_table_id;
      if (!linkedTableFactoryFormId) {
        continue;
      }

      const storedLinkedTable = inFlightStore.linkedTables?.[linkedTableFactoryFormId];

      if (storedLinkedTable) {
        if (!storedLinkedTable.items.find((item) => item.id === table_item_id)) {
          const linkedTableItem = await tableService.getTableItem(
            storedLinkedTable.table.table_name,
            table_item_id
          );
          if (linkedTableItem) {
            storedLinkedTable.items.push(linkedTableItem as TableItem);
          }
        }
        continue;
      }

      const tableItemData = await getTableItemDataFactory(linkedTableFactoryFormId, table_item_id);

      if (!tableItemData) {
        continue;
      }

      const { item, ...rest } = tableItemData;

      inFlightStore.linkedTables = inFlightStore.linkedTables || {};
      inFlightStore.linkedTables[linkedTableFactoryFormId] = {
        ...rest,
        items: [item as TableItem]
      };
    }
  }

  return inFlightStore;
}
